import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import ReactMarkdown from "react-markdown";
import TextEffect from "../../components/CustomTexts/TextEffect";
import bgIMG from "../../assets/Shabu-Training-2021/ok.jpg";
import Dropzone from "react-dropzone";

const UpdatingTrainingForm = () => {
  const { id } = useParams(); // Get the training ID from the URL
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTraining = async () => {
      const { data, error } = await supabase
        .from("trainings")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        alert("Error fetching training");
      } else {
        setTitle(data.title);
        setContent(data.content);
      }
    };

    fetchTraining();
  }, [id]);

  // Instant image preview when file is dropped
  useEffect(() => {
    if (!image) {
      setImagePreview(null);
      return;
    }
    const previewUrl = URL.createObjectURL(image);
    setImagePreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [image]);

  const handleUploadImage = async () => {
    if (!image) return null;

    const fileExt = image.name.split(".").pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const { error } = await supabase.storage
      .from("training-images")
      .upload(fileName, image);

    if (error) {
      console.error("Image upload error:", error.message);
      return null;
    }

    const { data: publicUrl } = supabase.storage
      .from("training-images")
      .getPublicUrl(fileName);

    return publicUrl.publicUrl;
  };

  const handleSubmit = async () => {
    setUploading(true);

    const imageUrl = await handleUploadImage();

    const { data, error } = await supabase
      .from("trainings")
      .update({
        title,
        content,
        image_url: imageUrl || undefined,
      })
      .eq("id", id)
      .select()
      .single();

    setUploading(false);

    if (error) {
      alert("Error updating training: " + error.message);
    } else {
      navigate(`/services/training/${data.training_name}`);
    }
  };

  return (
    <div>
      {/* Hero Header */}
      <div
        className="relative h-[60vh] flex items-center justify-center text-white bg-cover bg-center"
        style={{ backgroundImage: `url(${bgIMG})` }}
      >
        <div className="absolute inset-0 bg-black opacity-60 z-0" />
        <div className="z-10">
          <TextEffect text1="Update Training" className="hero-title" />
        </div>
      </div>

      {/* Editor Form */}
      <div className="px-4 sm:px-[5%] py-12 max-w-5xl mx-auto space-y-6">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Training Title"
          className="w-full p-3 border border-gray-300 rounded-md text-lg"
        />

        {/* Dropzone for Image */}
        <Dropzone onDrop={(acceptedFiles) => setImage(acceptedFiles[0])}>
          {({ getRootProps, getInputProps }) => (
            <div
              {...getRootProps()}
              className="cursor-pointer border-dashed border-2 p-6 text-center rounded-lg bg-gray-50 hover:bg-gray-100 transition"
            >
              <input {...getInputProps()} />
              {image ? (
                <p>Selected: {image.name}</p>
              ) : (
                <p>Drag & drop an image or click to upload</p>
              )}
            </div>
          )}
        </Dropzone>

        {/* Live image preview */}
        {imagePreview && (
          <img
            src={imagePreview}
            alt="Training Preview"
            className="w-full max-h-[300px] object-contain border rounded-md"
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Markdown Input */}
          <textarea
            rows={15}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your training content in markdown..."
            className="w-full p-3 border border-gray-300 rounded-md font-mono"
          />

          {/* Live Preview */}
          <div className="p-4 border border-gray-300 rounded-md bg-white overflow-auto">
            <div className="prose max-w-none">
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={uploading}
          className="bg-green-600 text-white px-6 py-3 rounded-lg shadow hover:bg-green-700 transition"
        >
          {uploading ? "Submitting..." : "Update Training"}
        </button>
      </div>
    </div>
  );
};

export default UpdatingTrainingForm;
function uuidv4() {
  throw new Error("Function not implemented.");
}

